import { create } from "zustand";

const RECENTLY_VISITED_PROJECTS_STORAGE_KEY = "recentlyVisitedProjects";
const MAX_RECENT_PROJECTS = 8;

export interface RecentlyVisitedProject {
  projectId: string;
  projectName: string;
  teamId: string;
  teamName: string;
  visitedAt: string;
}

const isRecentlyVisitedProject = (value: unknown): value is RecentlyVisitedProject => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.projectId === "string" &&
    typeof candidate.projectName === "string" &&
    typeof candidate.teamId === "string" &&
    typeof candidate.teamName === "string" &&
    typeof candidate.visitedAt === "string"
  );
};

const readRecentProjects = (): RecentlyVisitedProject[] => {
  try {
    const raw = localStorage.getItem(RECENTLY_VISITED_PROJECTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentlyVisitedProject) : [];
  } catch {
    return [];
  }
};

const persistRecentProjects = (projects: RecentlyVisitedProject[]) => {
  localStorage.setItem(RECENTLY_VISITED_PROJECTS_STORAGE_KEY, JSON.stringify(projects));
};

type RecentlyVisitedState = {
  recentProjects: RecentlyVisitedProject[];
  recordProjectVisit: (entry: Omit<RecentlyVisitedProject, "visitedAt">) => void;
  /**
  Called after a project is deleted, so a stale entry doesn't linger and later resolve to nothing.
  */
  forgetProject: (projectId: string) => void;
  /**
  Drops any entry whose project no longer appears in the authoritative list - covers a project
  deleted by someone else, which this browser otherwise has no way to learn about until it tries
  (and fails) to navigate to it. Called whenever the workspace's project list is (re)hydrated.
  */
  reconcileWithKnownProjects: (knownProjectIds: readonly string[]) => void;
};

/**
Most-recently-visited projects, newest first, capped at `MAX_RECENT_PROJECTS` and deduplicated by
project id - a client-side convenience for the Home page's quick-nav list, not synced to the backend.
*/
export const useRecentlyVisitedStore = create<RecentlyVisitedState>((set) => ({
  recentProjects: readRecentProjects(),
  recordProjectVisit: (entry) =>
    set((state) => {
      const withoutExisting = state.recentProjects.filter((project) => project.projectId !== entry.projectId);
      const nextProjects = [{ ...entry, visitedAt: new Date().toISOString() }, ...withoutExisting].slice(0, MAX_RECENT_PROJECTS);
      persistRecentProjects(nextProjects);
      return { recentProjects: nextProjects };
    }),
  forgetProject: (projectId) =>
    set((state) => {
      const nextProjects = state.recentProjects.filter((project) => project.projectId !== projectId);
      persistRecentProjects(nextProjects);
      return { recentProjects: nextProjects };
    }),
  reconcileWithKnownProjects: (knownProjectIds) =>
    set((state) => {
      const knownIds = new Set(knownProjectIds);
      const nextProjects = state.recentProjects.filter((project) => knownIds.has(project.projectId));
      if (nextProjects.length === state.recentProjects.length) return state;
      persistRecentProjects(nextProjects);
      return { recentProjects: nextProjects };
    }),
}));
